import { Component } from '@theme/component';

const ANIMATION_DURATION_MS = 1000;
const DEFAULT_INTERVAL_MS = 3500;

/**
 * Announcement strip with vertical slide-up animation: each slide enters from bottom,
 * stops at center, then after a delay the next slide enters and the current exits upward.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement} [playPauseButton]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBarCustom extends Component {
  static get observedAttributes() {
    return ['autoplay'];
  }

  #current = 0;
  /** @type {ReturnType<typeof setInterval> | null} */
  #interval = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #animationEndTimeout = null;
  #userPaused = false;

  /** @returns {HTMLButtonElement | undefined} */
  #getPlayPauseButton() {
    const raw = this.refs.playPauseButton;
    return /** @type {HTMLButtonElement | undefined} */ (Array.isArray(raw) ? raw[0] : raw);
  }

  get slides() {
    return this.refs.slides ?? [];
  }

  get slideCount() {
    return this.slides.length;
  }

  get autoplayInterval() {
    const val = this.getAttribute('autoplay');
    if (val == null || val === '') return DEFAULT_INTERVAL_MS;
    const parsed = parseInt(val, 10);
    return Number.isNaN(parsed) ? DEFAULT_INTERVAL_MS : parsed;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.slideCount <= 1) return;

    this.addEventListener('mouseenter', this.#suspend);
    this.addEventListener('mouseleave', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    const playPauseButton = this.#getPlayPauseButton();
    if (playPauseButton) {
      playPauseButton.addEventListener('click', this.#onPlayPauseClick);
    }

    this.#setSlideStates();
    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#suspend();
    this.#clearAnimationTimeout();
    const playPauseButton = this.#getPlayPauseButton();
    if (playPauseButton) {
      playPauseButton.removeEventListener('click', this.#onPlayPauseClick);
    }
    this.removeEventListener('mouseenter', this.#suspend);
    this.removeEventListener('mouseleave', this.#resume);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #onPlayPauseClick = () => {
    this.#userPaused = !this.#userPaused;
    if (this.#userPaused) {
      this.#suspend();
      this.setAttribute('paused', '');
    } else {
      this.#play();
      this.removeAttribute('paused');
    }
    this.#updatePlayPauseButton();
  };

  #updatePlayPauseButton() {
    const btn = this.#getPlayPauseButton();
    if (!btn) return;
    const pauseLabel = this.getAttribute('data-pause-label') || 'Pause';
    const playLabel = this.getAttribute('data-play-label') || 'Play';
    const pauseIcon = btn.querySelector('.announcement-bar-custom__play-pause-icon--pause');
    const playIcon = btn.querySelector('.announcement-bar-custom__play-pause-icon--play');
    if (this.#userPaused) {
      btn.setAttribute('aria-label', playLabel);
      pauseIcon?.classList.add('hidden');
      playIcon?.classList.remove('hidden');
    } else {
      btn.setAttribute('aria-label', pauseLabel);
      pauseIcon?.classList.remove('hidden');
      playIcon?.classList.add('hidden');
    }
  }

  #play() {
    if (this.slideCount <= 1) return;
    this.#suspend();
    this.#clearAnimationTimeout();
    this.#clearSlideAnimationClasses();
    this.#setSlideStates();
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.#next();
    }, this.autoplayInterval);
  }

  #clearSlideAnimationClasses() {
    this.slides.forEach((slide) => {
      slide.classList.remove('strip-slide-exiting', 'strip-slide-entering');
    });
  }

  #suspend = () => {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  };

  #resume = () => {
    if (document.hidden || this.slideCount <= 1 || this.#userPaused) return;
    this.#play();
  };

  #clearAnimationTimeout() {
    if (this.#animationEndTimeout) {
      clearTimeout(this.#animationEndTimeout);
      this.#animationEndTimeout = null;
    }
  }

  #onVisibilityChange = () => {
    if (document.hidden) this.#suspend();
    else this.#resume();
  };

  #next() {
    if (this.slideCount <= 1) return;

    const prevIndex = this.#current;
    this.#current = (this.#current + 1) % this.slideCount;
    const nextIndex = this.#current;

    const prevSlide = this.slides[prevIndex];
    const nextSlide = this.slides[nextIndex];

    if (!prevSlide || !nextSlide) return;

    prevSlide.setAttribute('inert', '');
    nextSlide.removeAttribute('inert');

    prevSlide.classList.add('strip-slide-exiting');
    nextSlide.classList.add('strip-slide-entering');

    this.#clearAnimationTimeout();
    this.#animationEndTimeout = setTimeout(() => {
      this.#animationEndTimeout = null;
      prevSlide.classList.remove('strip-slide-exiting');
      nextSlide.classList.remove('strip-slide-entering');
    }, ANIMATION_DURATION_MS);
  }

  #setSlideStates() {
    this.slides.forEach((slide, index) => {
      if (index === this.#current) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });
  }
}

if (!customElements.get('announcement-bar-custom')) {
  customElements.define('announcement-bar-custom', AnnouncementBarCustom);
}
